/**
 * Shared runtime for the conditional decoration plugins (conditional table *rows*
 * and conditional table *cells*). Both decorate the editor from outside React, so
 * they need one shared handle on the live handlebars evaluator + resolution-state
 * resolver the `DynamicNodeEditorProvider` publishes, and one shared set of refresh
 * listeners so every decoration recomputes when resolved data lands.
 *
 * Keeping this here (rather than in `conditional-table-row.ts`) means the row and
 * cell extensions share the *same* evaluator instance and refresh set — a single
 * `setConditionEvaluator` call drives both.
 */

/**
 * Resolution state of a condition, mirroring the conditional chip: whether the keys
 * it references resolve (`resolved`), are still loading, or can't be found
 * (`unresolved`). Drives the edge colour — independent of whether the condition
 * currently evaluates true (that only dims the row/cell).
 */
export type ConditionState = "resolved" | "loading" | "unresolved" | "empty";

/** The live handlebars evaluator + a resolution-state resolver keyed on the keys a
 * condition references + refresh listeners. */
let conditionEvaluator: ((template: string) => string) | null = null;
let conditionStateResolver: ((keys: string[]) => ConditionState) | null = null;
const refreshers = new Set<() => void>();

/**
 * Publish the handlebars evaluator and the resolution-state resolver (and trigger a
 * decoration refresh). Called by the editor provider whenever the resolved
 * answer/context data changes; called with `null` to tear down.
 */
export const setConditionEvaluator = (
  evaluator: ((template: string) => string) | null,
  stateResolver: ((keys: string[]) => ConditionState) | null = null
): void => {
  conditionEvaluator = evaluator;
  conditionStateResolver = stateResolver;
  refreshers.forEach((refresh) => refresh());
};

/**
 * Whether a full handlebars template currently has an active (non-empty) branch.
 * Defaults to active when no evaluator is published yet.
 */
export const isTemplateActive = (template: string): boolean => {
  if (!conditionEvaluator) {
    return true;
  }
  return conditionEvaluator(template).trim() !== "";
};

/** Resolution state for the keys a condition references; `empty` when unknown. */
export const keysConditionState = (keys: string[]): ConditionState =>
  conditionStateResolver ? conditionStateResolver(keys) : "empty";

/** Subscribe a decoration-refresh callback; returns an unsubscribe function. */
export const subscribeConditionRefresh = (refresh: () => void): (() => void) => {
  refreshers.add(refresh);
  return () => {
    refreshers.delete(refresh);
  };
};
